import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import apiService from '../services/api';

// Initial state
const initialState = {
  // Spots data
  spots: [],
  filteredSpots: [],
  selectedSpot: null,
  selectedSpotFromPin: null,
  
  // User data
  user: null,
  currentUser: null,
  userFavorites: [],
  userVisited: [],
  likedSpots: new Set(),
  likedPhotos: new Set(), // Set of photo URLs that user has liked
  
  // UI states
  searchQuery: '',
  searchFilter: 'all',
  selectedTags: [], // Array of selected tag slugs for filtering
  selectedTimeSlots: [], // Array of selected time slot IDs
  selectedMetadataFilters: {
    time_windows: [],    // ex: ['sunrise', 'sunset']
    weather: [],         // ex: ['clear', 'partly_cloudy']
    light: [],           // ex: ['golden_hour', 'blue_hour']
    crowd_level: [],     // ex: ['quiet', 'moderate']
    difficulty: [],      // ex: ['easy', 'moderate']
  }, // V2 metadata filters (system tags)
  tagCategories: [], // Available tag categories
  allTags: [], // All available tags
  maxDistance: 50,
  mapCenter: [46.2044, 6.1432], // Geneva coordinates
  userLocation: null,
  userLocationAccuracy: null, // GPS accuracy in meters
  showUserLocation: true, // Show user location marker on map
  centerOnUserLocation: false, // Auto-center map on user location
  mapLayer: 'osm',
  mapRef: null,
  mapWidth: 66.67,
  isDragging: false,
  mapInitialized: false,
  
  // Display mode for spots list
  spotsDisplayMode: 'list', // 'list' or 'grid'
  
  // Sorting options for spots
  sortMode: 'proximity', // 'proximity', 'date', 'popularity', 'title'
  sortOrder: 'asc', // 'asc' or 'desc'
  secondarySortMode: 'popularity', // Secondary sort criteria
  secondarySortOrder: 'desc', // Secondary sort order
  enableSecondarySorting: true, // Enable/disable secondary sorting
  
  // Gallery and photos
  allPhotosWithSpots: [],
  
  // Modal states
  showAddSpot: false,
  showSpotDetails: false,
  showAuth: false,
  showParkingMap: false,
  showParkingPhoto: false,
  showGPSOptions: false,
  showLightbox: false,
  lightboxImages: [],
  lightboxCurrentIndex: 0,
  showGallery: false,
  
  // TikTok Gallery states
  showTikTokGallery: false,
  tikTokGalleryMode: 'scroll',
  tikTokCurrentIndex: 0,
  showTikTokSpotDetails: false,
  tikTokSelectedSpot: null,
  tikTokComments: [],
  tikTokCurrentPhotoIndex: 0,
  tikTokSpotSpecificMode: false, // New: true when showing only one spot's photos
  tikTokTargetSpotId: null, // New: ID of the specific spot to show
  
  // Legal and share states
  showLegalModal: false,
  legalContent: '',
  legalTitle: '',
  shareDropdownOpen: null,
  urlParamsProcessed: false,
  
  // Form states
  newSpot: {
    title: '',
    description: '',
    latitude: 46.2044,
    longitude: 6.1432,
    tags: [],
    author: 'Anonymous'
  },
  newComment: '',
  comments: [],
  
  // Navigation states
  currentSpotIndex: 0,
  currentPhotoIndex: 0,
  
  // Loading states
  loading: false,
  error: null,
  
  // Authentication states
  tryingToAddSpot: false,
};

// Action types
const actionTypes = {
  // Spots actions
  SET_SPOTS: 'SET_SPOTS',
  SET_FILTERED_SPOTS: 'SET_FILTERED_SPOTS',
  SET_SELECTED_SPOT: 'SET_SELECTED_SPOT',
  SET_SELECTED_SPOT_FROM_PIN: 'SET_SELECTED_SPOT_FROM_PIN',
  ADD_SPOT: 'ADD_SPOT',
  UPDATE_SPOT: 'UPDATE_SPOT',
  DELETE_SPOT: 'DELETE_SPOT',
  
  // User actions
  SET_USER: 'SET_USER',
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  SET_USER_FAVORITES: 'SET_USER_FAVORITES',
  SET_USER_VISITED: 'SET_USER_VISITED',
  SET_LIKED_SPOTS: 'SET_LIKED_SPOTS',
  TOGGLE_LIKE_SPOT: 'TOGGLE_LIKE_SPOT',
  SET_LIKED_PHOTOS: 'SET_LIKED_PHOTOS',
  TOGGLE_LIKE_PHOTO: 'TOGGLE_LIKE_PHOTO',
  SET_TRYING_TO_ADD_SPOT: 'SET_TRYING_TO_ADD_SPOT',
  
  // UI actions
  SET_SEARCH_QUERY: 'SET_SEARCH_QUERY',
  SET_SEARCH_FILTER: 'SET_SEARCH_FILTER',
  SET_SELECTED_TAGS: 'SET_SELECTED_TAGS',
  SET_SELECTED_TIME_SLOTS: 'SET_SELECTED_TIME_SLOTS',
  SET_SELECTED_METADATA_FILTERS: 'SET_SELECTED_METADATA_FILTERS',
  TOGGLE_METADATA_FILTER: 'TOGGLE_METADATA_FILTER',
  CLEAR_METADATA_FILTERS: 'CLEAR_METADATA_FILTERS',
  SET_TAG_CATEGORIES: 'SET_TAG_CATEGORIES',
  SET_ALL_TAGS: 'SET_ALL_TAGS',
  SET_MAX_DISTANCE: 'SET_MAX_DISTANCE',
  SET_MAP_CENTER: 'SET_MAP_CENTER',
  SET_USER_LOCATION: 'SET_USER_LOCATION',
  SET_USER_LOCATION_ACCURACY: 'SET_USER_LOCATION_ACCURACY',
  SET_SHOW_USER_LOCATION: 'SET_SHOW_USER_LOCATION',
  SET_CENTER_ON_USER_LOCATION: 'SET_CENTER_ON_USER_LOCATION',
  SET_MAP_LAYER: 'SET_MAP_LAYER',
  SET_MAP_REF: 'SET_MAP_REF',
  SET_MAP_WIDTH: 'SET_MAP_WIDTH',
  SET_IS_DRAGGING: 'SET_IS_DRAGGING',
  SET_MAP_INITIALIZED: 'SET_MAP_INITIALIZED',
  SET_SPOTS_DISPLAY_MODE: 'SET_SPOTS_DISPLAY_MODE',
  
  // Sorting actions
  SET_SORT_MODE: 'SET_SORT_MODE',
  SET_SORT_ORDER: 'SET_SORT_ORDER',
  SET_SECONDARY_SORT_MODE: 'SET_SECONDARY_SORT_MODE',
  SET_SECONDARY_SORT_ORDER: 'SET_SECONDARY_SORT_ORDER',
  SET_ENABLE_SECONDARY_SORTING: 'SET_ENABLE_SECONDARY_SORTING',
  
  // Gallery actions
  SET_ALL_PHOTOS_WITH_SPOTS: 'SET_ALL_PHOTOS_WITH_SPOTS',
  
  // Modal actions
  SET_SHOW_ADD_SPOT: 'SET_SHOW_ADD_SPOT',
  SET_SHOW_SPOT_DETAILS: 'SET_SHOW_SPOT_DETAILS',
  SET_SHOW_AUTH: 'SET_SHOW_AUTH',
  SET_SHOW_PARKING_MAP: 'SET_SHOW_PARKING_MAP',
  SET_SHOW_PARKING_PHOTO: 'SET_SHOW_PARKING_PHOTO',
  SET_SHOW_GPS_OPTIONS: 'SET_SHOW_GPS_OPTIONS',
  SET_SHOW_LIGHTBOX: 'SET_SHOW_LIGHTBOX',
  SET_LIGHTBOX_IMAGES: 'SET_LIGHTBOX_IMAGES',
  SET_LIGHTBOX_CURRENT_INDEX: 'SET_LIGHTBOX_CURRENT_INDEX',
  SET_SHOW_GALLERY: 'SET_SHOW_GALLERY',
  
  // TikTok Gallery actions
  SET_SHOW_TIKTOK_GALLERY: 'SET_SHOW_TIKTOK_GALLERY',
  SET_TIKTOK_GALLERY_MODE: 'SET_TIKTOK_GALLERY_MODE',
  SET_TIKTOK_CURRENT_INDEX: 'SET_TIKTOK_CURRENT_INDEX',
  SET_SHOW_TIKTOK_SPOT_DETAILS: 'SET_SHOW_TIKTOK_SPOT_DETAILS',
  SET_TIKTOK_SELECTED_SPOT: 'SET_TIKTOK_SELECTED_SPOT',
  SET_TIKTOK_COMMENTS: 'SET_TIKTOK_COMMENTS',
  SET_TIKTOK_CURRENT_PHOTO_INDEX: 'SET_TIKTOK_CURRENT_PHOTO_INDEX',
  SET_TIKTOK_SPOT_SPECIFIC_MODE: 'SET_TIKTOK_SPOT_SPECIFIC_MODE',
  SET_TIKTOK_TARGET_SPOT_ID: 'SET_TIKTOK_TARGET_SPOT_ID',
  
  // Legal and share actions
  SET_SHOW_LEGAL_MODAL: 'SET_SHOW_LEGAL_MODAL',
  SET_LEGAL_CONTENT: 'SET_LEGAL_CONTENT',
  SET_LEGAL_TITLE: 'SET_LEGAL_TITLE',
  SET_SHARE_DROPDOWN_OPEN: 'SET_SHARE_DROPDOWN_OPEN',
  SET_URL_PARAMS_PROCESSED: 'SET_URL_PARAMS_PROCESSED',
  
  // Form actions
  SET_NEW_SPOT: 'SET_NEW_SPOT',
  SET_NEW_COMMENT: 'SET_NEW_COMMENT',
  SET_COMMENTS: 'SET_COMMENTS',
  ADD_COMMENT: 'ADD_COMMENT',
  
  // Navigation actions
  SET_CURRENT_SPOT_INDEX: 'SET_CURRENT_SPOT_INDEX',
  SET_CURRENT_PHOTO_INDEX: 'SET_CURRENT_PHOTO_INDEX',
  
  // Loading actions
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
};

// Reducer function
function appReducer(state, action) {
  switch (action.type) {
    // Spots reducers
    case actionTypes.SET_SPOTS:
      return { ...state, spots: action.payload };
    case actionTypes.SET_FILTERED_SPOTS:
      return { ...state, filteredSpots: action.payload };
    case actionTypes.SET_SELECTED_SPOT:
      return { ...state, selectedSpot: action.payload };
    case actionTypes.SET_SELECTED_SPOT_FROM_PIN:
      return { ...state, selectedSpotFromPin: action.payload };
    case actionTypes.ADD_SPOT:
      return { ...state, spots: [...state.spots, action.payload] };
    case actionTypes.UPDATE_SPOT:
      return {
        ...state,
        spots: state.spots.map(spot =>
          spot.id === action.payload.id ? action.payload : spot
        ),
        // Also update selectedSpot if it's the same spot
        selectedSpot: state.selectedSpot?.id === action.payload.id 
          ? action.payload 
          : state.selectedSpot
      };
    case actionTypes.DELETE_SPOT:
      return {
        ...state,
        spots: state.spots.filter(spot => spot.id !== action.payload)
      };
    
    // User reducers
    case actionTypes.SET_USER:
      return { ...state, user: action.payload };
    case actionTypes.SET_CURRENT_USER:
      return { ...state, currentUser: action.payload };
    case actionTypes.SET_USER_FAVORITES:
      return { ...state, userFavorites: action.payload };
    case actionTypes.SET_USER_VISITED:
      return { ...state, userVisited: action.payload };
    case actionTypes.SET_LIKED_SPOTS:
      return { ...state, likedSpots: action.payload };
    case actionTypes.TOGGLE_LIKE_SPOT:
      const newLikedSpots = new Set(state.likedSpots);
      if (newLikedSpots.has(action.payload)) {
        newLikedSpots.delete(action.payload);
      } else {
        newLikedSpots.add(action.payload);
      }
      return { ...state, likedSpots: newLikedSpots };
    case actionTypes.SET_LIKED_PHOTOS:
      return { ...state, likedPhotos: action.payload };
    case actionTypes.TOGGLE_LIKE_PHOTO:
      const newLikedPhotos = new Set(state.likedPhotos);
      if (newLikedPhotos.has(action.payload)) {
        newLikedPhotos.delete(action.payload);
      } else {
        newLikedPhotos.add(action.payload);
      }
      return { ...state, likedPhotos: newLikedPhotos };
    case actionTypes.SET_TRYING_TO_ADD_SPOT:
      return { ...state, tryingToAddSpot: action.payload };
    
    // UI reducers
    case actionTypes.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    case actionTypes.SET_SEARCH_FILTER:
      return { ...state, searchFilter: action.payload };
    case actionTypes.SET_SELECTED_TAGS:
      return { ...state, selectedTags: action.payload };
    case actionTypes.SET_SELECTED_TIME_SLOTS:
      return { ...state, selectedTimeSlots: action.payload };
    case actionTypes.SET_SELECTED_METADATA_FILTERS:
      return { ...state, selectedMetadataFilters: action.payload };
    case actionTypes.TOGGLE_METADATA_FILTER: {
      const { category, value } = action.payload;
      const currentFilters = state.selectedMetadataFilters;
      const categoryFilters = currentFilters[category] || [];
      const newCategoryFilters = categoryFilters.includes(value)
        ? categoryFilters.filter(v => v !== value)
        : [...categoryFilters, value];
      return { 
        ...state, 
        selectedMetadataFilters: { ...currentFilters, [category]: newCategoryFilters }
      };
    }
    case actionTypes.CLEAR_METADATA_FILTERS:
      return { 
        ...state, 
        selectedMetadataFilters: { time_windows: [], weather: [], light: [], crowd_level: [], difficulty: [] }
      };
    case actionTypes.SET_TAG_CATEGORIES:
      return { ...state, tagCategories: action.payload };
    case actionTypes.SET_ALL_TAGS:
      return { ...state, allTags: action.payload };
    case actionTypes.SET_MAX_DISTANCE:
      return { ...state, maxDistance: action.payload };
    case actionTypes.SET_MAP_CENTER:
      return { ...state, mapCenter: action.payload };
    case actionTypes.SET_USER_LOCATION:
      return { ...state, userLocation: action.payload };
    case actionTypes.SET_USER_LOCATION_ACCURACY:
      return { ...state, userLocationAccuracy: action.payload };
    case actionTypes.SET_SHOW_USER_LOCATION:
      return { ...state, showUserLocation: action.payload };
    case actionTypes.SET_CENTER_ON_USER_LOCATION:
      return { ...state, centerOnUserLocation: action.payload };
    case actionTypes.SET_MAP_LAYER:
      return { ...state, mapLayer: action.payload };
    case actionTypes.SET_MAP_REF:
      return { ...state, mapRef: action.payload };
    case actionTypes.SET_MAP_WIDTH:
      return { ...state, mapWidth: action.payload };
    case actionTypes.SET_IS_DRAGGING:
      return { ...state, isDragging: action.payload };
    case actionTypes.SET_MAP_INITIALIZED:
      return { ...state, mapInitialized: action.payload };
    case actionTypes.SET_SPOTS_DISPLAY_MODE:
      return { ...state, spotsDisplayMode: action.payload };
    
    // Sorting reducers
    case actionTypes.SET_SORT_MODE:
      return { ...state, sortMode: action.payload };
    case actionTypes.SET_SORT_ORDER:
      return { ...state, sortOrder: action.payload };
    case actionTypes.SET_SECONDARY_SORT_MODE:
      return { ...state, secondarySortMode: action.payload };
    case actionTypes.SET_SECONDARY_SORT_ORDER:
      return { ...state, secondarySortOrder: action.payload };
    case actionTypes.SET_ENABLE_SECONDARY_SORTING:
      return { ...state, enableSecondarySorting: action.payload };
    
    // Gallery reducers
    case actionTypes.SET_ALL_PHOTOS_WITH_SPOTS:
      return { ...state, allPhotosWithSpots: action.payload };
    
    // Modal reducers
    case actionTypes.SET_SHOW_ADD_SPOT:
      return { ...state, showAddSpot: action.payload };
    case actionTypes.SET_SHOW_SPOT_DETAILS:
      return { ...state, showSpotDetails: action.payload };
    case actionTypes.SET_SHOW_AUTH:
      return { ...state, showAuth: action.payload };
    case actionTypes.SET_SHOW_PARKING_MAP:
      return { ...state, showParkingMap: action.payload };
    case actionTypes.SET_SHOW_PARKING_PHOTO:
      return { ...state, showParkingPhoto: action.payload };
    case actionTypes.SET_SHOW_GPS_OPTIONS:
      return { ...state, showGPSOptions: action.payload };
    case actionTypes.SET_SHOW_LIGHTBOX:
      return { ...state, showLightbox: action.payload };
    case actionTypes.SET_LIGHTBOX_IMAGES:
      return { ...state, lightboxImages: action.payload };
    case actionTypes.SET_LIGHTBOX_CURRENT_INDEX:
      return { ...state, lightboxCurrentIndex: action.payload };
    case actionTypes.SET_SHOW_GALLERY:
      return { ...state, showGallery: action.payload };
    
    // TikTok Gallery reducers
    case actionTypes.SET_SHOW_TIKTOK_GALLERY:
      return { ...state, showTikTokGallery: action.payload };
    case actionTypes.SET_TIKTOK_GALLERY_MODE:
      return { ...state, tikTokGalleryMode: action.payload };
    case actionTypes.SET_TIKTOK_CURRENT_INDEX:
      return { ...state, tikTokCurrentIndex: action.payload };
    case actionTypes.SET_SHOW_TIKTOK_SPOT_DETAILS:
      return { ...state, showTikTokSpotDetails: action.payload };
    case actionTypes.SET_TIKTOK_SELECTED_SPOT:
      return { ...state, tikTokSelectedSpot: action.payload };
    case actionTypes.SET_TIKTOK_COMMENTS:
      return { ...state, tikTokComments: action.payload };
    case actionTypes.SET_TIKTOK_CURRENT_PHOTO_INDEX:
      return { ...state, tikTokCurrentPhotoIndex: action.payload };
    case actionTypes.SET_TIKTOK_SPOT_SPECIFIC_MODE:
      return { ...state, tikTokSpotSpecificMode: action.payload };
    case actionTypes.SET_TIKTOK_TARGET_SPOT_ID:
      return { ...state, tikTokTargetSpotId: action.payload };
    
    // Legal and share reducers
    case actionTypes.SET_SHOW_LEGAL_MODAL:
      return { ...state, showLegalModal: action.payload };
    case actionTypes.SET_LEGAL_CONTENT:
      return { ...state, legalContent: action.payload };
    case actionTypes.SET_LEGAL_TITLE:
      return { ...state, legalTitle: action.payload };
    case actionTypes.SET_SHARE_DROPDOWN_OPEN:
      return { ...state, shareDropdownOpen: action.payload };
    case actionTypes.SET_URL_PARAMS_PROCESSED:
      return { ...state, urlParamsProcessed: action.payload };
    
    // Form reducers
    case actionTypes.SET_NEW_SPOT:
      return { ...state, newSpot: action.payload };
    case actionTypes.SET_NEW_COMMENT:
      return { ...state, newComment: action.payload };
    case actionTypes.SET_COMMENTS:
      return { ...state, comments: action.payload };
    case actionTypes.ADD_COMMENT:
      return { ...state, comments: [...state.comments, action.payload] };
    
    // Navigation reducers
    case actionTypes.SET_CURRENT_SPOT_INDEX:
      return { ...state, currentSpotIndex: action.payload };
    case actionTypes.SET_CURRENT_PHOTO_INDEX:
      return { ...state, currentPhotoIndex: action.payload };
    
    // Loading reducers
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload };
    
    default:
      return state;
  }
}

// Create context
const AppContext = createContext();

// Context provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators (wrapped in useCallback to prevent re-renders)
  const actions = React.useMemo(() => ({
    // Spots actions
    setSpots: (spots) => dispatch({ type: actionTypes.SET_SPOTS, payload: spots }),
    setFilteredSpots: (spots) => dispatch({ type: actionTypes.SET_FILTERED_SPOTS, payload: spots }),
    setSelectedSpot: (spot) => dispatch({ type: actionTypes.SET_SELECTED_SPOT, payload: spot }),
    setSelectedSpotFromPin: (spot) => dispatch({ type: actionTypes.SET_SELECTED_SPOT_FROM_PIN, payload: spot }),
    addSpot: (spot) => dispatch({ type: actionTypes.ADD_SPOT, payload: spot }),
    updateSpot: (spot) => dispatch({ type: actionTypes.UPDATE_SPOT, payload: spot }),
    deleteSpot: (spotId) => dispatch({ type: actionTypes.DELETE_SPOT, payload: spotId }),
    
    // User actions
    setUser: (user) => dispatch({ type: actionTypes.SET_USER, payload: user }),
    setCurrentUser: (user) => dispatch({ type: actionTypes.SET_CURRENT_USER, payload: user }),
    setUserFavorites: (favorites) => dispatch({ type: actionTypes.SET_USER_FAVORITES, payload: favorites }),
    setUserVisited: (visited) => dispatch({ type: actionTypes.SET_USER_VISITED, payload: visited }),
    setLikedSpots: (spots) => dispatch({ type: actionTypes.SET_LIKED_SPOTS, payload: spots }),
    toggleLikeSpot: (spotId) => dispatch({ type: actionTypes.TOGGLE_LIKE_SPOT, payload: spotId }),
    setTryingToAddSpot: (trying) => dispatch({ type: actionTypes.SET_TRYING_TO_ADD_SPOT, payload: trying }),
    
    // UI actions
    setSearchQuery: (query) => dispatch({ type: actionTypes.SET_SEARCH_QUERY, payload: query }),
    setSearchFilter: (filter) => dispatch({ type: actionTypes.SET_SEARCH_FILTER, payload: filter }),
    setSelectedTags: (tags) => dispatch({ type: actionTypes.SET_SELECTED_TAGS, payload: tags }),
    setSelectedTimeSlots: (slots) => dispatch({ type: actionTypes.SET_SELECTED_TIME_SLOTS, payload: slots }),
    setSelectedMetadataFilters: (filters) => dispatch({ type: actionTypes.SET_SELECTED_METADATA_FILTERS, payload: filters }),
    // Helper to toggle a single metadata filter - dispatches action to reducer for proper state handling
    toggleMetadataFilter: (category, value) => {
      dispatch({ 
        type: actionTypes.TOGGLE_METADATA_FILTER, 
        payload: { category, value }
      });
    },
    // Clear all metadata filters
    clearMetadataFilters: () => {
      dispatch({ type: actionTypes.CLEAR_METADATA_FILTERS });
    },
    setTagCategories: (categories) => dispatch({ type: actionTypes.SET_TAG_CATEGORIES, payload: categories }),
    setAllTags: (tags) => dispatch({ type: actionTypes.SET_ALL_TAGS, payload: tags }),
    setMaxDistance: (distance) => dispatch({ type: actionTypes.SET_MAX_DISTANCE, payload: distance }),
    setMapCenter: (center) => dispatch({ type: actionTypes.SET_MAP_CENTER, payload: center }),
    setUserLocation: (location) => dispatch({ type: actionTypes.SET_USER_LOCATION, payload: location }),
    setUserLocationAccuracy: (accuracy) => dispatch({ type: actionTypes.SET_USER_LOCATION_ACCURACY, payload: accuracy }),
    setShowUserLocation: (show) => dispatch({ type: actionTypes.SET_SHOW_USER_LOCATION, payload: show }),
    setCenterOnUserLocation: (center) => dispatch({ type: actionTypes.SET_CENTER_ON_USER_LOCATION, payload: center }),
    setMapLayer: (layer) => dispatch({ type: actionTypes.SET_MAP_LAYER, payload: layer }),
    setMapRef: (ref) => dispatch({ type: actionTypes.SET_MAP_REF, payload: ref }),
    setMapWidth: (width) => dispatch({ type: actionTypes.SET_MAP_WIDTH, payload: width }),
    setIsDragging: (dragging) => dispatch({ type: actionTypes.SET_IS_DRAGGING, payload: dragging }),
    setMapInitialized: (initialized) => dispatch({ type: actionTypes.SET_MAP_INITIALIZED, payload: initialized }),
    setSpotsDisplayMode: (mode) => dispatch({ type: actionTypes.SET_SPOTS_DISPLAY_MODE, payload: mode }),
    
    // Sorting actions
    setSortMode: (mode) => dispatch({ type: actionTypes.SET_SORT_MODE, payload: mode }),
    setSortOrder: (order) => dispatch({ type: actionTypes.SET_SORT_ORDER, payload: order }),
    setSecondarySortMode: (mode) => dispatch({ type: actionTypes.SET_SECONDARY_SORT_MODE, payload: mode }),
    setSecondarySortOrder: (order) => dispatch({ type: actionTypes.SET_SECONDARY_SORT_ORDER, payload: order }),
    setEnableSecondarySorting: (enable) => dispatch({ type: actionTypes.SET_ENABLE_SECONDARY_SORTING, payload: enable }),
    
    // Gallery actions
    setAllPhotosWithSpots: (photos) => dispatch({ type: actionTypes.SET_ALL_PHOTOS_WITH_SPOTS, payload: photos }),
    
    // Modal actions
    setShowAddSpot: (show) => dispatch({ type: actionTypes.SET_SHOW_ADD_SPOT, payload: show }),
    setShowSpotDetails: (show) => dispatch({ type: actionTypes.SET_SHOW_SPOT_DETAILS, payload: show }),
    setShowAuth: (show) => dispatch({ type: actionTypes.SET_SHOW_AUTH, payload: show }),
    setShowParkingMap: (show) => dispatch({ type: actionTypes.SET_SHOW_PARKING_MAP, payload: show }),
    setShowParkingPhoto: (show) => dispatch({ type: actionTypes.SET_SHOW_PARKING_PHOTO, payload: show }),
    setShowGPSOptions: (show) => dispatch({ type: actionTypes.SET_SHOW_GPS_OPTIONS, payload: show }),
    setShowLightbox: (show) => dispatch({ type: actionTypes.SET_SHOW_LIGHTBOX, payload: show }),
    setLightboxImages: (images) => dispatch({ type: actionTypes.SET_LIGHTBOX_IMAGES, payload: images }),
    setLightboxCurrentIndex: (index) => dispatch({ type: actionTypes.SET_LIGHTBOX_CURRENT_INDEX, payload: index }),
    setShowGallery: (show) => dispatch({ type: actionTypes.SET_SHOW_GALLERY, payload: show }),
    
    // TikTok Gallery actions
    setShowTikTokGallery: (show) => dispatch({ type: actionTypes.SET_SHOW_TIKTOK_GALLERY, payload: show }),
    setTikTokGalleryMode: (mode) => dispatch({ type: actionTypes.SET_TIKTOK_GALLERY_MODE, payload: mode }),
    setTikTokCurrentIndex: (index) => dispatch({ type: actionTypes.SET_TIKTOK_CURRENT_INDEX, payload: index }),
    setShowTikTokSpotDetails: (show) => dispatch({ type: actionTypes.SET_SHOW_TIKTOK_SPOT_DETAILS, payload: show }),
    setTikTokSelectedSpot: (spot) => dispatch({ type: actionTypes.SET_TIKTOK_SELECTED_SPOT, payload: spot }),
    setTikTokComments: (comments) => dispatch({ type: actionTypes.SET_TIKTOK_COMMENTS, payload: comments }),
    setTikTokCurrentPhotoIndex: (index) => dispatch({ type: actionTypes.SET_TIKTOK_CURRENT_PHOTO_INDEX, payload: index }),
    setTikTokSpotSpecificMode: (isSpecific) => dispatch({ type: actionTypes.SET_TIKTOK_SPOT_SPECIFIC_MODE, payload: isSpecific }),
    setTikTokTargetSpotId: (spotId) => dispatch({ type: actionTypes.SET_TIKTOK_TARGET_SPOT_ID, payload: spotId }),
    
    // Helper function to open gallery for specific spot
    openSpotGallery: (spot, photoIndex = 0) => {
      dispatch({ type: actionTypes.SET_TIKTOK_TARGET_SPOT_ID, payload: spot.id });
      dispatch({ type: actionTypes.SET_TIKTOK_SPOT_SPECIFIC_MODE, payload: true });
      dispatch({ type: actionTypes.SET_TIKTOK_CURRENT_PHOTO_INDEX, payload: photoIndex });
      dispatch({ type: actionTypes.SET_TIKTOK_SELECTED_SPOT, payload: spot });
      dispatch({ type: actionTypes.SET_SHOW_TIKTOK_GALLERY, payload: true });
    },
    
    // Legal and share actions
    setShowLegalModal: (show) => dispatch({ type: actionTypes.SET_SHOW_LEGAL_MODAL, payload: show }),
    setLegalContent: (content) => dispatch({ type: actionTypes.SET_LEGAL_CONTENT, payload: content }),
    setLegalTitle: (title) => dispatch({ type: actionTypes.SET_LEGAL_TITLE, payload: title }),
    setShareDropdownOpen: (spotId) => dispatch({ type: actionTypes.SET_SHARE_DROPDOWN_OPEN, payload: spotId }),
    setUrlParamsProcessed: (processed) => dispatch({ type: actionTypes.SET_URL_PARAMS_PROCESSED, payload: processed }),
    
    // Form actions
    setNewSpot: (spot) => dispatch({ type: actionTypes.SET_NEW_SPOT, payload: spot }),
    setNewComment: (comment) => dispatch({ type: actionTypes.SET_NEW_COMMENT, payload: comment }),
    setComments: (comments) => dispatch({ type: actionTypes.SET_COMMENTS, payload: comments }),
    addComment: (comment) => dispatch({ type: actionTypes.ADD_COMMENT, payload: comment }),
    
    // Navigation actions
    setCurrentSpotIndex: (index) => dispatch({ type: actionTypes.SET_CURRENT_SPOT_INDEX, payload: index }),
    setCurrentPhotoIndex: (index) => dispatch({ type: actionTypes.SET_CURRENT_PHOTO_INDEX, payload: index }),
    
    // Loading actions
    setLoading: (loading) => dispatch({ type: actionTypes.SET_LOADING, payload: loading }),
    setError: (error) => dispatch({ type: actionTypes.SET_ERROR, payload: error }),
    
    // Combined actions with API calls
    async fetchSpots() {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      try {
        const spots = await apiService.getSpots();
        dispatch({ type: actionTypes.SET_SPOTS, payload: spots });
        
        // Initialize demo data if no spots exist
        if (spots.length === 0) {
          await apiService.initializeDemoData();
          const newSpots = await apiService.getSpots();
          dispatch({ type: actionTypes.SET_SPOTS, payload: newSpots });
        }
        
        dispatch({ type: actionTypes.SET_ERROR, payload: null });
      } catch (error) {
        dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
      } finally {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    },
    
    async createSpot(spotData) {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      try {
        const newSpot = await apiService.createSpot(spotData);
        dispatch({ type: actionTypes.ADD_SPOT, payload: newSpot });
        dispatch({ type: actionTypes.SET_ERROR, payload: null });
        return newSpot;
      } catch (error) {
        dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
        throw error;
      } finally {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    },
    
    async likeSpot(spotId, action = 'like') {
      try {
        await apiService.likeSpot(spotId, action);
        // Update the spot in the state
        const updatedSpot = await apiService.getSpotById(spotId);
        dispatch({ type: actionTypes.UPDATE_SPOT, payload: updatedSpot });
        dispatch({ type: actionTypes.TOGGLE_LIKE_SPOT, payload: spotId });
      } catch (error) {
        console.error('Error liking spot:', error);
        dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
      }
    },
    
    async likePhoto(photoUrl, spotId) {
      try {
        console.log('likePhoto action called with:', photoUrl, spotId);
        const response = await apiService.likePhoto(photoUrl, spotId);
        console.log('likePhoto response:', response);
        dispatch({ type: actionTypes.TOGGLE_LIKE_PHOTO, payload: photoUrl });
        return response;
      } catch (error) {
        console.error('Error in likePhoto action:', error);
        // Don't throw error - just log it and return null
        // This prevents the error from bubbling up and causing issues
        return null;
      }
    },
    
    async fetchLikedPhotos() {
      try {
        const likedPhotos = await apiService.getLikedPhotos();
        const photoUrls = new Set(likedPhotos.map(p => p.photo_url));
        dispatch({ type: actionTypes.SET_LIKED_PHOTOS, payload: photoUrls });
        return likedPhotos;
      } catch (error) {
        console.error('Error fetching liked photos:', error);
        // Don't throw error, just return empty array
        return [];
      }
    },
    
    async addComment(spotId, commentData) {
      try {
        const newComment = await apiService.addComment(spotId, commentData);
        dispatch({ type: actionTypes.ADD_COMMENT, payload: newComment });
        return newComment;
      } catch (error) {
        dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
        throw error;
      }
    },
    
    async fetchComments(spotId) {
      try {
        const comments = await apiService.getComments(spotId);
        dispatch({ type: actionTypes.SET_COMMENTS, payload: comments });
        return comments;
      } catch (error) {
        dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
        throw error;
      }
    },
  }), [dispatch]);

  return (
    <AppContext.Provider value={{ state, actions }}>
      {children}
    </AppContext.Provider>
  );
}

// Custom hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export { actionTypes };
export default AppContext;